import React, { FC, useMemo, useRef } from 'react';
import Select, { components } from 'react-select';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import { FilterErrorIndicator } from '@uikit/Tables/Filtration/constants';

interface IProps {
  title?: string;
  info?: any;
  IconLeft?: any;
  selectOptions: any;
  placeholder?: string;
  maxWidth?: number;
  disabled?: boolean;
  isMulti?: boolean;
  styles?: any;
  valueContainerStyles?: any;
  value?: any;
  removeCaret?: boolean;
  type?: string;
  warningIndicator?: FilterErrorIndicator;
  warningKey?: keyof FilterErrorIndicator;
  selectedOption: any;
  setSelectedOption: any;
  dataTestId?: string;
  setInputValue?: any;
}

export const CustomSelect: FC<IProps> = ({
  title,
  info,
  type,
  IconLeft,
  selectOptions,
  placeholder,
  maxWidth,
  disabled,
  isMulti,
  styles,
  valueContainerStyles,
  removeCaret = false,
  warningIndicator,
  warningKey = 'operator',
  selectedOption,
  setSelectedOption,
  dataTestId,
  setInputValue,
}) => {
  const selectRef = useRef<any>(null);

  const normalizedOptions = useMemo(
    () =>
      selectOptions?.map((item: any) => ({
        value: item?.value ?? item?.name,
        label: item?.label,
        isDisabled: Boolean(item?.isDisabled ?? item?.disabled),
        ...item,
      })) || [],
    [selectOptions]
  );

  const handleSelectChange = (selected: any) => {
    if (selected?.isDisabled) return;
    setSelectedOption(selected);
    setInputValue(null);
  };

  const customComponents = useMemo(() => {
    const baseComponents: any = {
      Control: (props: any) => (
        <components.Control
          {...props}
          innerProps={{ ...props.innerProps, 'data-test-id': dataTestId }}
        />
      ),
      Input: (props: any) => (
        <components.Input {...props} data-test-id={`${dataTestId}-input`} />
      ),
      Option: (props: any) => (
        <components.Option
          {...props}
          innerProps={{
            ...props.innerProps,
            'data-test-id': `${dataTestId}-option-${props.data.value}`,
          }}
        />
      ),
    };

    if (removeCaret || disabled) {
      baseComponents.DropdownIndicator = () => null;
      baseComponents.IndicatorSeparator = () => null;
    }

    return baseComponents;
  }, [removeCaret, disabled, dataTestId]);

  return (
    <div
      style={{
        maxWidth: maxWidth,
        border:
          typeof warningIndicator === 'object' && warningIndicator?.[warningKey]
            ? '1px solid red'
            : 'none',
      }}
      className="input-text filtration-select"
    >
      {info?.toggle && (
        <InfoTooltip className="info_shower" title={info?.desc}>
          <div></div>
        </InfoTooltip>
      )}
      <div className={!disabled ? '' : 'disabled'}>
        <div className="input-title-wrapper">
          {title && <span className="input-title">{title}</span>}
        </div>
        <InfoTooltip title={selectedOption?.label}>
          <div style={{ width: '100%' }}>
            {IconLeft && typeof IconLeft === 'function' && (
              <div className="select-icon">
                <IconLeft />
              </div>
            )}

            <Select
              ref={selectRef}
              classNamePrefix="custom-select"
              value={selectedOption}
              onChange={handleSelectChange}
              options={normalizedOptions}
              isOptionDisabled={(option) =>
                Boolean(option?.isDisabled ?? option?.disabled)
              }
              placeholder={placeholder}
              isMulti={isMulti}
              isDisabled={disabled}
              components={customComponents}
              styles={{
                placeholder: (provided) => ({
                  ...provided,
                  fontSize: '14px',
                }),
                control: (provided, state) => ({
                  ...provided,
                  borderColor: disabled
                    ? '#ccc'
                    : state.isFocused
                      ? '#6D31ED'
                      : '#9095A1',
                  paddingLeft: IconLeft ? 24 : 0,
                  background: disabled ? '#e9ecef' : '#f8f9fa',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  boxShadow: 'none',
                  '&:focus-within': {
                    borderColor: state.isFocused ? '#6D31ED' : '#9095A1',
                  },
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  ...valueContainerStyles,
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  alignItems: 'flex-start',
                }),
                indicatorSeparator: () => ({ display: 'none' }),
                // option: (provided, state) => ({
                //   ...provided,
                //   fontSize: '12px',
                //   color: state.isDisabled ? '#fff' : '#000',
                //   backgroundColor: state.isDisabled
                //     ? // ? 'rgba(45, 10, 118, 0.45)'
                //       'red'
                //     : state.isSelected
                //       ? '#2D0A76'
                //       : provided.backgroundColor,
                //   cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                //   '&:hover': state.isDisabled
                //     ? {}
                //     : {
                //         backgroundColor: '#2D0A76',
                //         color: '#fff',
                //       },
                // }),
                option: (provided, state) => ({
                  ...provided,
                  fontSize: '12px',
                  color: state.isDisabled
                    ? '#fff'
                    : state.isFocused || state.isSelected
                      ? '#fff'
                      : '#000',
                  backgroundColor: state.isDisabled
                    ? 'red'
                    : state.isSelected
                      ? '#2D0A76'
                      : state.isFocused
                        ? '#2D0A76'
                        : '#fff',
                }),
              }}
            />
          </div>
        </InfoTooltip>
      </div>
    </div>
  );
};
